import { memo, useEffect, useRef } from "react";
import classNames from "classnames/bind";
import { ESIInclude } from "../../../__shared__/ESIInclude/ESIInclude";
import styles from "./SocialProofMessages.module.scss";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import { encodeExperiencesObject } from "@/utils/monetate/encodeExperiencesObject";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { spmSessionStorageName } from "@/components/Galleries/utils/utils.ts";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

const cx = classNames.bind(styles);

export const SocialProofMessages = memo(function SocialProofMessages({
  productId,
  brand,
  availableToOrder,
  appId,
  experiencesRawObject = [],
  withClose = true,
  className = "",
  showSocialProofMessages = true,
  setShowSocialProofMessages,
  fadeOutSocialProofMessages = false,
  monetateEnvironment = "",
}: {
  productId: string;
  brand: string;
  availableToOrder: boolean;
  appId: string;
  withClose?: boolean;
  className?: string;
  experiencesRawObject: Experiences;
  showSocialProofMessages?: boolean;
  setShowSocialProofMessages: (arg: boolean) => void;
  fadeOutSocialProofMessages?: boolean;
  monetateEnvironment?: string;
}) {
  const { monetateId } = useMonetateId();
  const filteredExperiences = experiencesRawObject?.filter(
    ({ parameters }) => parameters?.fragment === "social-proof-messaging",
  );

  const url = useRef(
    `/esi/social-proof-messaging-ui/?${new URLSearchParams({
      productId,
      brand,
      appId,
      availableToOrder: availableToOrder ? "true" : "false",
      withClose: withClose ? "true" : "false",
      ...(filteredExperiences.length > 0
        ? {
            experiences: encodeExperiencesObject(filteredExperiences),
          }
        : {}),
    }).toString()}`,
  );

  const sendManagedImpressionEvent = () => {
    recordImpressionByParam(
      monetateEnvironment,
      experiencesRawObject,
      "pdp-scaffold-showSocialProofMessages",
      showSocialProofMessages ? "true" : "false",
      monetateId,
    );
  };

  const updateCloseClickCount = () => {
    const spmClosedCount = sessionStorage.getItem(spmSessionStorageName);
    if (spmClosedCount) {
      const updatedClosedCount = Number(spmClosedCount) + 1;
      sessionStorage.setItem(
        spmSessionStorageName,
        updatedClosedCount.toString(),
      );

      if (updatedClosedCount > 1) {
        dispatchMonetateQEvent("pdp:spm:close-clicked:gt-1");
      }
      if (updatedClosedCount > 2) {
        dispatchMonetateQEvent("pdp:spm:close-clicked:gt-2");
      }
    } else {
      sessionStorage.setItem(spmSessionStorageName, "1");
    }
  };

  useEffect(() => {
    const id = `jl-social-proof-messaging-${productId}`;

    const targetNode = document.getElementById(id);

    if (!targetNode) {
      return;
    }

    if (
      window.matchMedia("(max-width: 1023px)").matches &&
      Number(sessionStorage.getItem(spmSessionStorageName)) >= 2
    ) {
      setShowSocialProofMessages(false);
      return;
    }

    const config = { childList: true, subtree: true };

    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          if (document.querySelector('[data-testid="social-proof-messages"]')) {
            sendManagedImpressionEvent();
          }
        }
      }
    };

    const observer = new MutationObserver(callback);

    observer.observe(targetNode, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleClick = () => {
      updateCloseClickCount();
      setShowSocialProofMessages(false);
    };

    let intervalId: NodeJS.Timeout | null = null;
    let button: HTMLButtonElement | undefined = undefined;

    intervalId = setInterval(() => {
      const buttons = document.querySelectorAll<HTMLButtonElement>(
        "[data-testid='social-proof-messages'] button",
      );
      if (buttons?.length > 0) {
        button = buttons[0] ?? undefined;
        button?.addEventListener("click", handleClick);

        if (intervalId) clearInterval(intervalId);
      }
    }, 20);

    return () => {
      if (button) button?.removeEventListener("click", handleClick);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return (
    productId &&
    brand &&
    appId && (
      <div
        data-testid={`social-proof-messaging-${productId}`}
        id={`jl-social-proof-messaging-${productId}`}
        className={cx(
          "esi-wrapper",
          styles.socialProofMessagesEsiWrapper,
          {
            hidden: !showSocialProofMessages,
            fadeOut: fadeOutSocialProofMessages,
          },
          className,
        )}
      >
        <ESIInclude
          id={`jl-social-proof-messaging-${productId}`}
          src={url.current}
        />
      </div>
    )
  );
});
